/**
 * Coalesces concurrent requests for the same device's dashboard/summary info
 * (device/v2/deviceInfo/{deviceId}/{deviceType}/dashboard/{_Id}) into one network call.
 * Multiple independent components — the page breadcrumb title, and each device type's own
 * Summary tab — fetch this same endpoint under different apiConfig keys (GET_BR_SUMMARY_DATA,
 * GET_SWITCH_SUMMARY_DATA, ...) that all resolve to the identical URL and response shape.
 * Sharing the in-flight promise avoids the duplicate request; the entry clears once settled
 * so a later, deliberate refetch (e.g. after a reboot) still gets fresh data.
 */
import useApi from 'hooks/useApi';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

const inFlightRequests = new Map();

/**
 * @param {string} apiKey - apiConfig key to use for this call (e.g. 'GET_SWITCH_SUMMARY_DATA')
 * @param {Object} params - params passed through to useApi
 * @param {string} deviceId
 * @param {string} deviceType
 * @returns {Promise<Object>}
 */
export function fetchDeviceDashboardInfo(apiKey, params, deviceId, deviceType) {
  const userId = cookies.get('_Id');
  if (!deviceId || !deviceType || !userId) {
    return Promise.reject(new Error('fetchDeviceDashboardInfo: missing deviceId, deviceType, or _Id cookie'));
  }

  const key = `${deviceId}|${deviceType}|${userId}`;
  const existing = inFlightRequests.get(key);
  if (existing) return existing;

  const request = (async () => {
    try {
      return await useApi(apiKey, params, {}, []);
    } finally {
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, request);
  return request;
}
